import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { PurchaseConsentDialog } from './PurchaseConsentDialog';
import { usePaddleCheckout } from '@/hooks/usePaddleCheckout';

/**
 * Compact top-up row for the unified wallet. Top-ups add to the same balance,
 * never expire and are spent last. The checkout and consent flow is unchanged.
 *
 * Pricing note (2026-08-27 audit): these packs are still priced in USD by
 * Paddle and currently work out at a *lower* rate per credit than the Pro and
 * Lab subscriptions. Repricing needs new Paddle price IDs (target: €6.99 / 30
 * and €11.99 / 55). Until then the copy avoids advertising top-ups as the
 * cheaper route.
 */
interface TopUpPack {
  packType: 'starter' | 'value';
  name: string;
  price: string;
  credits: number;
}

export const TOP_UP_PACKS: TopUpPack[] = [
  { packType: 'starter', name: 'Small top-up', price: '$5.99', credits: 30 },
  { packType: 'value', name: 'Large top-up', price: '$9.99', credits: 55 },
];

export const TopUpPacks = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { openCheckout } = usePaddleCheckout();
  const [loadingPack, setLoadingPack] = useState<string | null>(null);
  const [consentOpen, setConsentOpen] = useState(false);
  const [selected, setSelected] = useState<TopUpPack | null>(null);

  const handleClick = (pack: TopUpPack) => {
    if (!user) {
      sessionStorage.setItem('redirectAfterAuth', '/pricing');
      navigate('/auth');
      return;
    }
    setSelected(pack);
    setConsentOpen(true);
  };

  const handleConfirm = async () => {
    if (!user || !selected) return;
    setLoadingPack(selected.packType);
    try {
      const { data, error } = await supabase.functions.invoke('create-paddle-checkout', {
        body: { packType: selected.packType, userId: user.id },
      });
      if (error) throw error;
      if (!data?.transactionId) throw new Error('No transaction ID received');
      setConsentOpen(false);
      openCheckout(data.transactionId);
    } catch (error) {
      console.error('Error creating top-up checkout:', error);
      toast.error('Unable to start checkout. Please try again.');
      setConsentOpen(false);
    } finally {
      setLoadingPack(null);
    }
  };

  return (
    <section aria-labelledby="topups-heading" className="max-w-3xl mx-auto w-full">
      <div className="text-center mb-4">
        <h2 id="topups-heading" className="text-base font-semibold">
          Need more before the month resets?
        </h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Top-ups add to the same balance, never expire and are spent last. One-off,
          billed in USD.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {TOP_UP_PACKS.map((pack) => (
          <Card key={pack.packType} className="bg-muted/20 border-border/60">
            <CardContent className="flex items-center justify-between gap-4 py-4">
              <div>
                <p className="text-sm font-medium">
                  {pack.name} · {pack.credits} credits
                </p>
                <p className="text-xs text-muted-foreground">{pack.price} one-time</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleClick(pack)}
                disabled={loadingPack === pack.packType}
              >
                {loadingPack === pack.packType ? 'Loading...' : 'Add credits'}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {selected && user && (
        <PurchaseConsentDialog
          open={consentOpen}
          onOpenChange={setConsentOpen}
          tier={{
            name: selected.name,
            price: selected.price,
            reviews: selected.credits,
            packType: selected.packType,
          }}
          userId={user.id}
          onConfirm={handleConfirm}
          isLoading={loadingPack === selected.packType}
        />
      )}
    </section>
  );
};

export default TopUpPacks;
